// apps/client/src/hooks/useWebsiteConnections.ts
//
// Website connections (Lead Capture > Website). One callable,
// `websiteConnectionAction` (client-functions, workstream A), carries every
// read and write:
//
//   list            -> { connections, referralPartners: [{ id, name }] }
//   create / update -> { connection }
//   rotateSiteKey   -> { connection }
//   setStatus       -> { connection }
//   resumeWelcome   -> { connection }
//   health          -> { days, lastSubmissionAt }
//
// super_admin may write; marketing may read (list / get / health). Founders
// manage nothing here: founder = admin portal only, and the callable refuses
// every founder write (blockFounderClientWrite). The server is the authority
// on all of this: this hook only reflects what it returns.
// companyId is sent the way every Lead Capture callable sends it; the server
// checks it against the caller's token claims (enforceCompanyAccess) and
// never trusts it on its own.
//
// Timestamps cross the callable boundary as whatever the server serialised
// (ISO string, epoch ms, or Firestore Timestamp JSON {_seconds}); readers use
// timestampToMillis below rather than assuming one shape.
//
// Spec: docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §3 and §9.

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
// Sub-path imports: the shared-schemas barrel pulls Node-only modules
// (crypto) that CRA 5 does not polyfill. websiteConnection is zod-only.
import type {
  ProtectionMode,
  WebsiteConnectionForms,
  WebsiteConnectionInput,
  WebsiteFormKind,
} from '@valutten/shared-schemas/dist/websiteConnection';
import { functions } from '../lib/firebase';

export const WEBSITE_CONNECTION_CALLABLE = 'websiteConnectionAction';

/** A connection as the callable returns it (timestamps in any serialised shape). */
export interface WebsiteConnectionView {
  id: string;
  companyId: string;
  label: string;
  environment: 'production' | 'staging';
  allowedOrigins: string[];
  mode: ProtectionMode;
  bot: { provider: 'recaptcha_enterprise'; minScore: number };
  forms: WebsiteConnectionForms;
  siteKey: string;
  previousSiteKeys: Array<{ key: string; expiresAt: unknown }>;
  generation: number;
  status: 'active' | 'disabled';
  welcome: { pausedReason: string | null; pausedAt: unknown };
  canary?: { enabled: boolean };
  createdAt: unknown;
  createdBy: string;
  updatedAt: unknown;
  updatedBy: string;
}

/**
 * A Partner Network partner this company operates
 * (partner.operatingCompanyId === companyId), offered as the target of the
 * website referral form. Returned by `list` alongside the connections.
 */
export interface WebsiteReferralPartnerOption {
  partnerId: string;
  name: string;
}

/**
 * What the editor saves. `canary` (the daily synthetic submission, spec §10)
 * is sent on update only: the tenant super_admin turns it on per connection,
 * and create always stores it off.
 */
export type WebsiteConnectionSaveInput = WebsiteConnectionInput & { canary?: { enabled: boolean } };

/** One Brisbane day of counters (companies/{c}/website_connection_stats/{id}_{yyyymmdd}). */
export interface WebsiteHealthDay {
  /** 'YYYY-MM-DD'. */
  date: string;
  submissions: Record<WebsiteFormKind, number>;
  /** Rejection reason -> count. */
  rejections: Record<string, number>;
  /** Bot score bucket lower bound ('0.0' ... '0.9') -> count. */
  botScoreBuckets: Record<string, number>;
  welcome: { sent: number; bounced: number; complained: number };
}

export interface WebsiteConnectionHealth {
  /** Oldest first, exactly `HEALTH_WINDOW_DAYS` long (missing days are zero). */
  days: WebsiteHealthDay[];
  lastSubmissionAtMs: number | null;
}

export const HEALTH_WINDOW_DAYS = 30;

// Mirrors websiteConnectionAction's RequestSchema (website/websiteConnectionAction.ts):
// create and update carry the input NESTED under `connection`, never spread
// flat. The contract test in pages/leads/website/__tests__ parses these
// payloads with WebsiteConnectionInputSchema and pins the server source.
export type CreateConnectionRequest = { action: 'create'; companyId: string; connection: WebsiteConnectionInput };
export type UpdateConnectionRequest = {
  action: 'update';
  companyId: string;
  connectionId: string;
  connection: WebsiteConnectionSaveInput;
};

type Action =
  | { action: 'list'; companyId: string }
  | CreateConnectionRequest
  | UpdateConnectionRequest
  | { action: 'rotateSiteKey'; companyId: string; connectionId: string }
  | { action: 'setStatus'; companyId: string; connectionId: string; status: 'active' | 'disabled' }
  | { action: 'resumeWelcome'; companyId: string; connectionId: string }
  | { action: 'health'; companyId: string; connectionId: string };

async function call<T>(payload: Action): Promise<T> {
  const fn = httpsCallable<Action, T>(functions, WEBSITE_CONNECTION_CALLABLE);
  const res = await fn(payload);
  return res.data;
}

// ─── Pure helpers (exported for tests and the pages) ────────────────────────

/** The `create` request body websiteConnectionAction accepts. */
export function buildCreateRequest(companyId: string, input: WebsiteConnectionInput): CreateConnectionRequest {
  return { action: 'create', companyId, connection: input };
}

/**
 * The `list` response's referralPartners ({ id, name }; `partnerId` is also
 * read so an older server keeps working), kept only where each entry has an
 * id, de-duplicated, sorted by name. Null when the server sent no list,
 * which the picker shows as "could not be loaded" rather than "none".
 */
export function normaliseReferralPartners(raw: unknown): WebsiteReferralPartnerOption[] | null {
  if (!Array.isArray(raw)) return null;
  const out: WebsiteReferralPartnerOption[] = [];
  for (const p of raw as Array<Record<string, unknown> | null>) {
    if (!p || typeof p !== 'object') continue;
    const rawId = typeof p.id === 'string' && p.id.trim() ? p.id : p.partnerId;
    const partnerId = typeof rawId === 'string' ? rawId.trim() : '';
    if (!partnerId || out.some((o) => o.partnerId === partnerId)) continue;
    const name = typeof p.name === 'string' && p.name.trim() ? p.name.trim() : partnerId;
    out.push({ partnerId, name });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name) || a.partnerId.localeCompare(b.partnerId));
}

/** The `update` request body websiteConnectionAction accepts. */
export function buildUpdateRequest(
  companyId: string,
  connectionId: string,
  input: WebsiteConnectionSaveInput,
): UpdateConnectionRequest {
  return { action: 'update', companyId, connectionId, connection: input };
}

/**
 * Epoch ms from any serialised timestamp: ISO string, epoch ms, Date,
 * Firestore Timestamp, or its JSON form ({seconds} / {_seconds}).
 * Null when the value is absent or unreadable.
 */
export function timestampToMillis(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
  }
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value === 'object') {
    const v = value as { toMillis?: unknown; seconds?: unknown; _seconds?: unknown; nanoseconds?: unknown; _nanoseconds?: unknown };
    if (typeof v.toMillis === 'function') {
      const ms = (v.toMillis as () => number)();
      return Number.isFinite(ms) ? ms : null;
    }
    const seconds = typeof v.seconds === 'number' ? v.seconds : typeof v._seconds === 'number' ? v._seconds : null;
    if (seconds === null) return null;
    const nanos = typeof v.nanoseconds === 'number' ? v.nanoseconds : typeof v._nanoseconds === 'number' ? v._nanoseconds : 0;
    return seconds * 1000 + Math.floor(nanos / 1e6);
  }
  return null;
}

/**
 * A user-facing message from a callable error. HttpsError messages written
 * by our functions are plain English and shown as is; transport and
 * internal failures get the caller's fallback.
 */
export function callableErrorMessage(err: unknown, fallback: string): string {
  const e = err as { code?: unknown; message?: unknown } | null;
  const code = typeof e?.code === 'string' ? e.code.replace(/^functions\//, '') : '';
  if (code === 'permission-denied') return 'You do not have permission to do this.';
  if (code === 'unauthenticated') return 'Your session has ended. Sign in again and retry.';
  if (code === 'internal' || code === 'unavailable' || code === 'deadline-exceeded' || code === 'unknown') {
    return fallback;
  }
  const message = typeof e?.message === 'string' ? e.message.trim() : '';
  if (!message || message.toUpperCase() === message) return fallback; // 'INTERNAL', 'NOT_FOUND'
  return message;
}

const FORM_KINDS: readonly WebsiteFormKind[] = ['enquiry', 'newsletter', 'referral'];

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;
}

function countMap(v: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!v || typeof v !== 'object') return out;
  for (const [k, n] of Object.entries(v as Record<string, unknown>)) {
    const c = num(n);
    if (c > 0) out[k] = c;
  }
  return out;
}

/** 'YYYY-MM-DD' for an 8-digit yyyymmdd or an ISO-ish date; null otherwise. */
function normaliseDay(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  if (/^\d{8}$/.test(raw)) return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(raw);
  return m ? m[1] : null;
}

/** 'YYYY-MM-DD' of `ms` on the Brisbane calendar (UTC+10, no daylight saving). */
export function brisbaneDay(ms: number): string {
  return new Date(ms + 10 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/**
 * Shape the raw `health` response into a fixed 30-day window ending on
 * `nowMs`'s Brisbane day. Missing days are zero, unknown fields are ignored,
 * so a partially written stats doc never breaks the panel.
 */
export function normaliseHealth(raw: unknown, nowMs: number): WebsiteConnectionHealth {
  const r = (raw ?? {}) as { days?: unknown; lastSubmissionAt?: unknown };
  const byDay = new Map<string, WebsiteHealthDay>();
  if (Array.isArray(r.days)) {
    for (const d of r.days as Array<Record<string, unknown>>) {
      if (!d || typeof d !== 'object') continue;
      const date = normaliseDay(d.date ?? d.day ?? d.yyyymmdd);
      if (!date) continue;
      const subs = (d.submissions ?? {}) as Record<string, unknown>;
      const welcome = (d.welcome ?? {}) as Record<string, unknown>;
      byDay.set(date, {
        date,
        submissions: { enquiry: num(subs.enquiry), newsletter: num(subs.newsletter), referral: num(subs.referral) },
        rejections: countMap(d.rejections),
        botScoreBuckets: countMap(d.botScoreBuckets ?? d.botScores),
        welcome: { sent: num(welcome.sent), bounced: num(welcome.bounced), complained: num(welcome.complained) },
      });
    }
  }
  const days: WebsiteHealthDay[] = [];
  for (let i = HEALTH_WINDOW_DAYS - 1; i >= 0; i -= 1) {
    const date = brisbaneDay(nowMs - i * 24 * 60 * 60 * 1000);
    days.push(
      byDay.get(date) ?? {
        date,
        submissions: { enquiry: 0, newsletter: 0, referral: 0 },
        rejections: {},
        botScoreBuckets: {},
        welcome: { sent: 0, bounced: 0, complained: 0 },
      },
    );
  }
  return { days, lastSubmissionAtMs: timestampToMillis(r.lastSubmissionAt) };
}

/** Window totals for the health panel. */
export function summariseHealth(health: WebsiteConnectionHealth): {
  submissions: Record<WebsiteFormKind, number>;
  rejections: Array<{ reason: string; count: number }>;
  welcome: { sent: number; bounced: number; complained: number };
} {
  const submissions: Record<WebsiteFormKind, number> = { enquiry: 0, newsletter: 0, referral: 0 };
  const rejections = new Map<string, number>();
  const welcome = { sent: 0, bounced: 0, complained: 0 };
  for (const d of health.days) {
    for (const f of FORM_KINDS) submissions[f] += d.submissions[f];
    for (const [reason, n] of Object.entries(d.rejections)) rejections.set(reason, (rejections.get(reason) ?? 0) + n);
    welcome.sent += d.welcome.sent;
    welcome.bounced += d.welcome.bounced;
    welcome.complained += d.welcome.complained;
  }
  return {
    submissions,
    rejections: Array.from(rejections, ([reason, count]) => ({ reason, count })).sort(
      (a, b) => b.count - a.count || a.reason.localeCompare(b.reason),
    ),
    welcome,
  };
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export interface UseWebsiteConnectionsResult {
  connections: WebsiteConnectionView[];
  /** Null when the server did not return a partner list (picker shows a notice). */
  referralPartners: WebsiteReferralPartnerOption[] | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  /** Each write resolves to the saved connection; rejects with a user-facing Error. */
  create: (input: WebsiteConnectionInput) => Promise<WebsiteConnectionView>;
  /**
   * Rejects (after showing the stored state) when the server saved the
   * connection but its canary setting differs from the one sent.
   */
  update: (connectionId: string, input: WebsiteConnectionSaveInput) => Promise<WebsiteConnectionView>;
  rotateSiteKey: (connectionId: string) => Promise<WebsiteConnectionView>;
  setStatus: (connectionId: string, status: 'active' | 'disabled') => Promise<WebsiteConnectionView>;
  resumeWelcome: (connectionId: string) => Promise<WebsiteConnectionView>;
  loadHealth: (connectionId: string) => Promise<WebsiteConnectionHealth>;
}

export function useWebsiteConnections(companyId: string | undefined): UseWebsiteConnectionsResult {
  const [connections, setConnections] = useState<WebsiteConnectionView[]>([]);
  const [referralPartners, setReferralPartners] = useState<WebsiteReferralPartnerOption[] | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    call<{ connections?: WebsiteConnectionView[]; referralPartners?: unknown }>({
      action: 'list',
      companyId,
    })
      .then((data) => {
        if (cancelled) return;
        setConnections(Array.isArray(data?.connections) ? data.connections : []);
        setReferralPartners(normaliseReferralPartners(data?.referralPartners));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[useWebsiteConnections] list failed', err);
        setError(callableErrorMessage(err, 'Website connections could not be loaded. Refresh to try again.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, refreshKey]);

  /** Replace (or add) one connection in local state from a write's response. */
  const applySaved = useCallback((saved: WebsiteConnectionView) => {
    setConnections((prev) => {
      const i = prev.findIndex((c) => c.id === saved.id);
      if (i === -1) return [...prev, saved];
      const next = prev.slice();
      next[i] = saved;
      return next;
    });
  }, []);

  const write = useCallback(
    async (payload: Action, fallback: string): Promise<WebsiteConnectionView> => {
      if (!companyId) throw new Error('No company is selected.');
      let saved: WebsiteConnectionView | undefined;
      try {
        const data = await call<{ connection?: WebsiteConnectionView }>(payload);
        saved = data?.connection;
      } catch (err) {
        throw new Error(callableErrorMessage(err, fallback));
      }
      if (!saved || typeof saved.id !== 'string') {
        // The write landed but the response carried no connection: reload so
        // the screen shows the stored state rather than a guess.
        refresh();
        throw new Error('Saved, but the updated connection was not returned. The list has been reloaded.');
      }
      applySaved(saved);
      return saved;
    },
    [applySaved, companyId, refresh],
  );

  const create = useCallback(
    (input: WebsiteConnectionInput) =>
      write(buildCreateRequest(companyId ?? '', input), 'The connection was not created. Please try again.'),
    [companyId, write],
  );

  const update = useCallback(
    async (connectionId: string, input: WebsiteConnectionSaveInput) => {
      const saved = await write(
        buildUpdateRequest(companyId ?? '', connectionId, input),
        'The connection was not saved. Please try again.',
      );
      // A server that does not know the field drops it and still answers
      // success; say so rather than let the toggle look applied.
      if (input.canary && (saved.canary?.enabled === true) !== input.canary.enabled) {
        throw new Error(
          `Saved, but the daily test submission is still ${saved.canary?.enabled === true ? 'on' : 'off'}. Please try again.`,
        );
      }
      return saved;
    },
    [companyId, write],
  );

  const rotateSiteKey = useCallback(
    (connectionId: string) =>
      write({ action: 'rotateSiteKey', companyId: companyId ?? '', connectionId }, 'The site key was not rotated. Please try again.'),
    [companyId, write],
  );

  const setStatus = useCallback(
    (connectionId: string, status: 'active' | 'disabled') =>
      write({ action: 'setStatus', companyId: companyId ?? '', connectionId, status }, 'The status was not changed. Please try again.'),
    [companyId, write],
  );

  const resumeWelcome = useCallback(
    (connectionId: string) =>
      write({ action: 'resumeWelcome', companyId: companyId ?? '', connectionId }, 'Welcome emails were not resumed. Please try again.'),
    [companyId, write],
  );

  const loadHealth = useCallback(
    async (connectionId: string): Promise<WebsiteConnectionHealth> => {
      if (!companyId) throw new Error('No company is selected.');
      try {
        const data = await call<unknown>({ action: 'health', companyId, connectionId });
        return normaliseHealth(data, Date.now());
      } catch (err) {
        throw new Error(callableErrorMessage(err, 'Health could not be loaded. Refresh to try again.'));
      }
    },
    [companyId],
  );

  return {
    connections,
    referralPartners,
    loading,
    error,
    refresh,
    create,
    update,
    rotateSiteKey,
    setStatus,
    resumeWelcome,
    loadHealth,
  };
}
